import React, { useEffect, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Table,
    Badge,
    UncontrolledTooltip
} from 'reactstrap';
import {
    FaUser,
    FaBuilding,
    FaPlus,
    FaTrash,
    FaSave,
    FaIdCard,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaMoneyBillWave,
    FaCreditCard,
    FaWallet,
    FaBusinessTime,
    FaUserTie,
    FaCheckCircle,
    FaEdit,
    FaEye
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import { Field } from 'formik';
import { supplierActions } from '../../sagas/supplierSlice';

const FournisseurForm = () => {
    const navigate = useNavigate();
    const params = useParams();

    // Fournisseur state
    const [fournisseur, setFournisseur] = useState({
        nom: '',
        prenom: '',
        cin: '',
        telephone1: '',
        telephone2: '',
        email: '',
        adresse: '',
        ville: 'Casablanca',
        typeFournisseur: 'PARTICULIER',
        codePostal: '',
        plafond: 0,
        entreprise: '', // New field for "Entreprise"
        ice: '',// New field for "ICE",
        active: true,
        solde: 0 // Ce champ sera en lecture seule
    });

    const location = useLocation();

    const fournisseurId = params.id; // For module routes: /fournisseurs/:id/edit
    const fournisseurEdit = location.state?.fournisseur || null; // For legacy routes
    console.log(fournisseurEdit)
    useEffect(() => {
        // Load fournisseur data from state (legacy routes)
        if (fournisseurEdit) {
            setFournisseur({
                id: fournisseurEdit.id || null,
                nom: fournisseurEdit.nom || '',
                prenom: fournisseurEdit.prenom || '',
                cin: fournisseurEdit.cin || '',
                telephone1: fournisseurEdit.telephone1 || '',
                telephone2: fournisseurEdit.telephone2 || '',
                typeFournisseur: fournisseurEdit.typeFournisseur || 'PARTICULIER',
                email: fournisseurEdit.email || '',
                adresse: fournisseurEdit.adresse || '',
                ville: fournisseurEdit.ville || 'Casablanca',
                codePostal: fournisseurEdit.codePostal || '',
                entreprise: fournisseurEdit.entreprise || '', // New field for "Entreprise"
                active: fournisseurEdit.active === true, // Ensure active is a boolean
                ice: fournisseurEdit.ice || '',// New field for "ICE",
            });

        }
    }, [fournisseurEdit, fournisseurId]);

    // UI state
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState('fournisseur');
    const { t } = useTranslation();
    const dispatch = useDispatch(); // Assuming you are using Redux for state management

    // Villes marocaines
    const villesMaroc = [
        'Casablanca', 'Rabat', 'Khouribga', 'Marrakech', 'Fès', 'Tanger',
        'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan',
        'Safi', 'Mohammedia', 'El Jadida', 'Nador', 'Beni Mellal'
    ];

    // Handlers
    const handleFournisseurChange = (e) => {
        const { name, value } = e.target;
        setFournisseur((prev) => ({
            ...prev,
            [name]: name === 'active' ? value === 'true' : value, // Convert "true"/"false" to boolean for the active field
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const newErrors = {};
        // Common validations
        if (!fournisseur.telephone1) newErrors.telephone1 = 'Téléphone obligatoire';
        if (!fournisseur.adresse) newErrors.adresse = 'Adresse obligatoire';

        // Validations for "PARTICULIER"
        if (fournisseur.typeFournisseur === 'PARTICULIER') {
            if (!fournisseur.nom) newErrors.nom = 'Nom obligatoire';
            if (!fournisseur.prenom) newErrors.prenom = 'Prénom obligatoire';
        }

        // Validations for "ENTREPRISE"
        if (fournisseur.typeFournisseur === 'ENTREPRISE') {
            if (!fournisseur.entreprise) newErrors.entreprise = 'Nom de l\'entreprise obligatoire';
            if (!fournisseur.ice) newErrors.ice = 'Numéro ICE obligatoire';
        }


        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let payload = {
            data: {
                ...fournisseur,
            },

            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: 'Succès',
                    text: 'Fournisseur enregistrés avec succès!',
                    confirmButtonText: 'OK',
                });

                setFournisseur({
                    nom: '',
                    prenom: '',
                    cin: '',
                    telephone1: '',
                    telephone2: '',
                    typeFournisseur: 'PARTICULIER',
                    email: '',
                    adresse: '',
                    ville: 'Casablanca',
                    codePostal: '',
                    plafond: 0,
                    entreprise: '',
                    ice: '',
                    solde: 0,
                    active: true,
                });
                if (location.pathname.startsWith('/purchase-module')) {
                    navigate('/purchase-module/fournisseurs');
                } else {
                    navigate(-1);
                }
            },
        };

        if (fournisseurEdit) {
            payload = {
                ...payload,
                data: {
                    ...payload.data,
                    id: fournisseurEdit.id,
                }
            };
            console.log(payload)
            dispatch(supplierActions.updateSupplier(payload));
        } else {
            dispatch(supplierActions.addSupplier(payload));
        }
    };

    return (
        <Container fluid>
            {/* Header Section */}
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                {fournisseurEdit ? 'Modifier Fournisseur' : 'Nouveau Fournisseur'}
                            </h4>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Success Alert */}
            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert className="fournisseur-form-success-alert text-center">
                            <FaCheckCircle className="me-2" />
                            <strong>Client enregistré avec succès!</strong>
                            <p className="mb-0 mt-2">Les informations ont été sauvegardées dans le système.</p>
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Navigation Tabs */}
            <Row className="mb-4">
                <Col>
                    <div className="navigation-tabs">
                        <div className="tab-container">
                            <Button
                                className={`tab-button ${activeTab === 'fournisseur' ? 'active' : ''}`}
                                onClick={() => setActiveTab('fournisseur')}
                                id="clientTabBtn"
                            >
                                <div className="tab-content">
                                    <div className={`tab-icon ${activeTab === 'fournisseur' ? 'active' : ''}`}>
                                        <FaUser size={16} />
                                    </div>
                                    <div className="tab-text">
                                        <span className="tab-label">Informations Fournisseur</span>
                                        <span className="tab-description">Données de base</span>
                                    </div>
                                </div>
                            </Button>
                        </div>
                    </div>
                    <style>
                        {`
                        .navigation-tabs {
                            background: #fff;
                            border-radius: 12px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.08);
                            padding: 1rem;
                        }
                        
                        .tab-container {
                            display: flex;
                            gap: 1rem;
                            flex-wrap: wrap;
                        }
                        
                        .tab-button {
                            background: transparent !important;
                            border: none !important;
                            padding: 1rem !important;
                            border-radius: 10px !important;
                            flex: 1;
                            min-width: 200px;
                            transition: all 0.3s ease;
                        }
                        
                        .tab-button:hover:not(:disabled) {
                            background: #f8f9fa !important;
                            transform: translateY(-2px);
                        }
                        
                        .tab-button.active {
                            background: #f8f9fa !important;
                            border: 1px solid #e9ecef !important;
                        }
                        
                        .tab-button:disabled {
                            opacity: 0.6;
                            cursor: not-allowed;
                        }
                        
                        .tab-content {
                            display: flex;
                            align-items: center;
                            gap: 1rem;
                        }
                        
                        .tab-icon {
                            width: 40px;
                            height: 40px;
                            border-radius: 8px;
                            background: #e9ecef;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: #6c757d;
                            transition: all 0.3s ease;
                        }
                        
                        .tab-icon.active {
                            background: #0d6efd;
                            color: white;
                        }
                        
                        .tab-text {
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            text-align: left;
                        }
                        
                        .tab-label {
                            font-weight: 600;
                            color: #212529;
                            margin-bottom: 0.25rem;
                        }
                        
                        .tab-description {
                            font-size: 0.875rem;
                            color: #6c757d;
                        }
                        
                        @media (max-width: 768px) {
                            .tab-button {
                                min-width: 100%;
                            }
                        }
                        `}
                    </style>
                </Col>
            </Row>
            <Form onSubmit={handleSubmit} className="compact-form">
                <style>
                    {`
                        .compact-form .form-control-sm {
                            min-height: 30px;
                            font-size: 0.92rem;
                            padding: 0.25rem 0.5rem;
                        }
                        .compact-form .form-floating > .form-control-sm {
                            padding-top: 0.9rem;
                        }
                        .compact-form .form-floating > label {
                            padding-top: 0.3rem;
                            opacity: 0.65;
                            font-size: 0.92rem;
                        }
                        .compact-form .card-body {
                            padding: 1rem;
                        }
                        .compact-form .row,
                        .compact-form .fournisseur-form-row {
                            margin-bottom: 0.25rem !important;
                        }
                        .compact-form .form-group,
                        .compact-form .form-floating {
                            margin-bottom: 0.5rem !important;
                        }
                        .compact-form .mb-4 {
                            margin-bottom: 0.5rem !important;
                        }
                        .compact-form .g-3 {
                            --bs-gutter-y: 0.5rem;
                        }
                        .compact-form .btn {
                            padding: 0.35rem 0.75rem;
                            font-size: 0.92rem;
                        }
                        .compact-form .form-floating > .bg-light {
                            background-color: #f8f9fa !important;
                        }
                        .compact-form .form-floating > .bg-light:focus {
                            background-color: #ffffff !important;
                        }
                        .compact-form .fournisseur-type-buttons {
                            margin-bottom: 0.75rem !important;
                        }
                        .compact-form .fournisseur-form-actions {
                            margin-top: 0.5rem;
                        }
                    `}
                </style>
                {/* Client Information Tab */}
                {activeTab === 'fournisseur' && (
                    <Card className="fournisseur-form-card border-0 shadow-sm">
                        <CardBody className="fournisseur-form-section p-4">
                            <style>
                                {`
                                    .form-floating {
                                        margin-bottom: 1rem;
                                    }
                                    .form-floating > .form-control-sm {
                                        height: calc(2.5rem + 2px);
                                        padding: 0.5rem 0.75rem;
                                        font-size: 0.875rem;
                                        border-radius: 0.375rem;
                                    }
                                    .form-floating > label {
                                        padding: 0.5rem 0.75rem;
                                        font-size: 0.875rem;
                                    }
                                    .form-control-sm {
                                        transition: all 0.2s ease-in-out;
                                    }
                                    .form-control-sm:focus {
                                        background-color: #fff !important;
                                        box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
                                        border-color: #86b7fe !important;
                                    }
                                    .fournisseur-type-buttons {
                                        display: inline-flex;
                                        background: #f8f9fa;
                                        padding: 0.25rem;
                                        border-radius: 0.5rem;
                                        gap: 0.25rem;
                                        margin-bottom: 1.5rem;
                                    }
                                    .fournisseur-type-btn {
                                        border: none;
                                        background: transparent;
                                        padding: 0.5rem 1rem;
                                        border-radius: 0.375rem;
                                        display: flex;
                                        align-items: center;
                                        gap: 0.5rem;
                                        font-size: 0.875rem;
                                        color: #6c757d;
                                        transition: all 0.2s;
                                    }
                                    .fournisseur-type-btn.active {
                                        background: white;
                                        color: #0d6efd;
                                        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                                    }
                                    .fournisseur-type-btn:hover:not(.active) {
                                        background: rgba(255,255,255,0.5);
                                    }
                                    .required-field::after {
                                        content: ' *';
                                        color: #dc3545;
                                    }
                                    .g-3 {
                                        --bs-gutter-y: 1rem;
                                    }
                                `}
                            </style>
                            {/* Client Type Selection */}
                            <div className="fournisseur-type-toggle">
                                <div className="fournisseur-type-buttons">
                                    <button
                                        type="button"
                                        className={`fournisseur-type-btn ${fournisseur.typeFournisseur === 'PARTICULIER' ? 'active' : ''}`}
                                        onClick={() => handleFournisseurChange({ target: { name: 'typeFournisseur', value: 'PARTICULIER' } })}
                                    >
                                        <FaUser />
                                        Particulier
                                    </button>
                                    <button
                                        type="button"
                                        className={`fournisseur-type-btn ${fournisseur.typeFournisseur === 'ENTREPRISE' ? 'active' : ''}`}
                                        onClick={() => handleFournisseurChange({ target: { name: 'typeFournisseur', value: 'ENTREPRISE' } })}
                                    >
                                        <FaBuilding />
                                        Entreprise
                                    </button>
                                </div>
                            </div>

                            {/* Particulier Fields */}
                            {fournisseur.typeFournisseur === 'PARTICULIER' && (
                                <Row className="fournisseur-form-row">
                                    <Col md={4}>
                                        <FormGroup className="fournisseur-form-group">
                                            <Label for="nom" className="fournisseur-form-label required-field">
                                                <FaUser />
                                                Nom
                                            </Label>
                                            <Input
                                                type="text"
                                                id="nom"
                                                name="nom"
                                                value={fournisseur.nom}
                                                onChange={handleFournisseurChange}
                                                className={`fournisseur-form-input ${errors.nom ? 'is-invalid' : ''}`}
                                                placeholder="El Fassi"
                                            />
                                            {errors.nom && <div className="fournisseur-form-error">{errors.nom}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup className="fournisseur-form-group">
                                            <Label for="prenom" className="fournisseur-form-label required-field">
                                                <FaUser />
                                                Prénom
                                            </Label>
                                            <Input
                                                type="text"
                                                id="prenom"
                                                name="prenom"
                                                value={fournisseur.prenom}
                                                onChange={handleFournisseurChange}
                                                className={`fournisseur-form-input ${errors.prenom ? 'is-invalid' : ''}`}
                                                placeholder="Karim"
                                            />
                                            {errors.prenom && <div className="fournisseur-form-error">{errors.prenom}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup className="fournisseur-form-group">
                                            <Label for="cin" className="fournisseur-form-label">
                                                <FaIdCard />
                                                CIN
                                            </Label>
                                            <Input
                                                type="text"
                                                id="cin"
                                                name="cin"
                                                value={fournisseur.cin}
                                                onChange={handleFournisseurChange}
                                                className="fournisseur-form-input"
                                                placeholder="AB123456"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                            {/* Entreprise Fields */}
                            {fournisseur.typeFournisseur === 'ENTREPRISE' && (
                                <Row className="fournisseur-form-row">
                                    <Col md={6}>
                                        <FormGroup className="fournisseur-form-group">
                                            <Label for="entreprise" className="fournisseur-form-label required-field">
                                                <FaBuilding />
                                                Nom de l'entreprise
                                            </Label>
                                            <Input
                                                type="text"
                                                id="entreprise"
                                                name="entreprise"
                                                value={fournisseur.entreprise}
                                                onChange={handleFournisseurChange}
                                                className={`fournisseur-form-input ${errors.entreprise ? 'is-invalid' : ''}`}
                                                placeholder="Nom de l'entreprise"
                                            />
                                            {errors.entreprise && <div className="fournisseur-form-error">{errors.entreprise}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup className="fournisseur-form-group">
                                            <Label for="ice" className="fournisseur-form-label required-field">
                                                <FaIdCard />
                                                Numéro ICE
                                            </Label>
                                            <Input
                                                type="text"
                                                id="ice"
                                                name="ice"
                                                value={fournisseur.ice}
                                                onChange={handleFournisseurChange}
                                                className={`fournisseur-form-input ${errors.ice ? 'is-invalid' : ''}`}
                                                placeholder="000123456789012"
                                            />
                                            {errors.ice && <div className="fournisseur-form-error">{errors.ice}</div>}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                            <Row className="g-3">
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="telephone1">
                                            <FaPhone className="me-2" size={12} />
                                            Téléphone (1) *
                                        </Label>
                                        <Input
                                            type="tel"
                                            id="telephone1"
                                            name="telephone1"
                                            value={fournisseur.telephone1}
                                            onChange={handleFournisseurChange}
                                            invalid={!!errors.telephone1}
                                            placeholder="06 12 34 56 78"
                                            className="py-2"
                                        />

                                        {errors.telephone1 && <div className="text-danger small mt-1">{errors.telephone1}</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="telephone2">
                                            <FaPhone className="me-2" size={12} />
                                            Téléphone (2) *
                                        </Label>
                                        <Input
                                            type="tel"
                                            id="telephone2"
                                            name="telephone2"
                                            value={fournisseur.telephone2}
                                            onChange={handleFournisseurChange}
                                            invalid={!!errors.telephone2}
                                            placeholder="06 12 34 56 78"
                                            className="py-2"
                                        />

                                        {errors.telephone2 && <div className="text-danger small mt-1">{errors.telephone2}</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="email" lassName="fw-bold">
                                            <FaEnvelope className="me-2" size={12} />
                                            Email
                                        </Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={fournisseur.email}
                                            onChange={handleFournisseurChange}
                                            placeholder="k.elfassi@example.com"
                                            className="py-2"
                                        />

                                    </FormGroup>
                                </Col>


                            </Row>
                            <Row>
                                <Col md={8}>
                                    <FormGroup className="mb-4">
                                        <Label for="adresse" className="fw-bold">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            Adresse*
                                        </Label>
                                        <Input
                                            type="textarea"
                                            rows="2"
                                            id="adresse"
                                            name="adresse"
                                            value={fournisseur.adresse}
                                            onChange={handleFournisseurChange}
                                            placeholder="12 Rue Moulay Rachid"
                                            className="py-2"
                                        />
                                        {errors.adresse && <div className="text-danger small mt-1">{errors.adresse}</div>}
                                    </FormGroup>
                                </Col>

                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="ville" className="fw-bold">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            Ville
                                        </Label>
                                        <Input
                                            type="select"
                                            id="ville"
                                            name="ville"
                                            value={fournisseur.ville}
                                            onChange={handleFournisseurChange}
                                            className="py-2"
                                        >
                                            {villesMaroc.map(ville => (
                                                <option key={ville} value={ville}>{ville}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="active" className="fw-bold">
                                            <FaBusinessTime className="me-2 text-muted" />
                                            Statut Actif*
                                        </Label>
                                        <Input
                                            type="select"
                                            id="active"
                                            name="active"
                                            value={fournisseur.active.toString()} // Convert boolean to string for the select element
                                            onChange={handleFournisseurChange}
                                            className="py-2"
                                        >
                                            <option value="true">Actif</option>
                                            <option value="false">Inactif</option>
                                        </Input>
                                    </FormGroup>
                                </Col>

                            </Row>
                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                        <Input
                                            type="textarea"
                                            name="commentaire"
                                            rows="3"
                                            className="form-control"
                                            placeholder={t('reglement.commentairePlaceholder')}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <div className="fournisseur-form-actions">
                                <Button
                                    type="submit"
                                    className="fournisseur-form-btn fournisseur-form-btn-success"
                                    id="saveClientBtn"
                                >
                                    <FaSave />
                                    Enregistrer
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </Form>

        </Container>
    );
};

export default FournisseurForm;